// src/modules/meridian/components/documentType/MeridianStructureAgent/ChatPanel.tsx
//
// Chat panel component using mmagent-chat-ui for the Structure Agent.

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Input,
  CircularProgress,
  Tooltip,
  Alert,
} from "@mui/joy";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import StopIcon from "@mui/icons-material/Stop";
import {
  ChatProvider,
  useChat,
  useChatStore,
  ChatMessage,
} from "@metalmind-ab/mmagent-chat-ui";
import type { ChatMessageType } from "@metalmind-ab/mmagent-chat-ui";
import "@metalmind-ab/mmagent-chat-ui/styles.css";
import "../../../styles/meridian-agent-theme.css";

import { useAuthHeaders } from "../../../../../hooks/useAuthHeaders";
import { getApiBaseUrl } from "../../../../../config/runtimeConfig";
import { buildChatResumeOptions } from "../../../../../utils/chatResumeOptions";

interface ChatPanelProps {
  documentTypeId: number;
  documentTypeName: string;
  workspaceId: string;
  onSectionsChanged: () => void;
  onClose: () => void;
}

/**
 * Chat panel component that provides the agent chat interface.
 */
export const ChatPanel: React.FC<ChatPanelProps> = ({
  documentTypeId,
  documentTypeName,
  workspaceId,
  onSectionsChanged,
  onClose,
}) => {
  const [authHeaders, setAuthHeaders] = useState<Record<string, string> | null>(
    null,
  );
  const [authError, setAuthError] = useState<string | null>(null);
  const getAuthHeaders = useAuthHeaders();

  // Get store actions to reset state when context changes
  // This prevents chat history from bleeding between different document types or modules
  const clearMessages = useChatStore((state) => state.clearMessages);
  const setConversationId = useChatStore((state) => state.setConversationId);
  const setConversations = useChatStore((state) => state.setConversations);
  const setLoading = useChatStore((state) => state.setLoading);

  // Clear chat store on mount and when document type changes
  // This is critical because the Zustand store is global and shared across all chat instances
  useEffect(() => {
    clearMessages();
    setConversationId(null);
    setConversations([], 0);
    setLoading(false);
  }, [
    workspaceId,
    documentTypeId,
    clearMessages,
    setConversationId,
    setConversations,
    setLoading,
  ]);

  // Fetch auth headers on mount
  useEffect(() => {
    const fetchHeaders = async () => {
      try {
        const headers = await getAuthHeaders();
        setAuthHeaders(headers);
      } catch (err) {
        setAuthError((err as Error).message);
      }
    };
    fetchHeaders();
  }, [getAuthHeaders]);

  if (authHeaders === null && !authError) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size="sm" />
      </Box>
    );
  }

  if (authError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography level="body-sm" color="danger">
          Authentication failed: {authError}
        </Typography>
      </Box>
    );
  }

  const apiBaseUrl = `${getApiBaseUrl()}/api/workspace/meridian/structure-agent`;

  const headers: Record<string, string> = {
    ...authHeaders,
    "X-Workspace-Id": workspaceId,
    "X-Document-Type-Id": String(documentTypeId),
  };

  return (
    <ChatProvider
      key={`${workspaceId}-${documentTypeId}`}
      config={{
        apiBaseUrl,
        headers,
        cancelSubmission: true,
        // Task #580 Phase 3b: SSE Last-Event-ID resume against BE broker.
        ...buildChatResumeOptions(apiBaseUrl),
      }}
    >
      <ChatContent
        documentTypeName={documentTypeName}
        onSectionsChanged={onSectionsChanged}
        onClose={onClose}
      />
    </ChatProvider>
  );
};

interface ChatContentProps {
  documentTypeName: string;
  onSectionsChanged: () => void;
  onClose: () => void;
}

const ChatContent: React.FC<ChatContentProps> = ({
  documentTypeName,
  onSectionsChanged,
  onClose,
}) => {
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // mmagent-chat-ui hooks
  const {
    messages,
    sendMessage,
    isLoading,
    cancelStream,
    isStopping,
    stopError,
  } = useChat();
  useChatStore();
  const prevIsLoadingRef = useRef(isLoading);
  const cancelStreamRef = useRef(cancelStream);
  cancelStreamRef.current = cancelStream;

  useEffect(() => {
    return () => {
      cancelStreamRef.current();
    };
  }, []);

  // Refresh sections when agent completes a response (loading changes from true to false)
  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading) {
      // Agent just finished responding - refresh sections in case they were modified
      onSectionsChanged();
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading, onSectionsChanged]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;

    const message = inputValue.trim();
    setInputValue("");
    await sendMessage(message);
  }, [inputValue, isLoading, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.level1",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography level="title-sm" noWrap>
            Structure Assistant
          </Typography>
          <Typography level="body-xs" sx={{ color: "text.tertiary" }} noWrap>
            {documentTypeName}
          </Typography>
        </Box>
        <IconButton size="sm" variant="plain" color="neutral" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {messages.length === 0 ? (
          <Box sx={{ py: 3, px: 0.5 }}>
            <Typography
              level="body-sm"
              sx={{ color: "text.tertiary", textAlign: "center", mb: 2 }}
            >
              Ask me to help design the section structure for this document
              type.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[
                "Create a standard contract structure with introduction, terms, and signatures",
                "Add an executive summary section at the top",
                "Suggest sections for a financial report",
              ].map((prompt) => (
                <Button
                  key={prompt}
                  variant="outlined"
                  color="neutral"
                  size="sm"
                  disabled={isLoading}
                  onClick={async () => {
                    setInputValue("");
                    await sendMessage(prompt);
                  }}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    fontWeight: "normal",
                    whiteSpace: "normal",
                    height: "auto",
                    py: 1,
                  }}
                >
                  {prompt}
                </Button>
              ))}
            </Box>
          </Box>
        ) : (
          messages.map((message: ChatMessageType) => (
            <ChatMessage key={message.id} message={message} />
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.level1",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Input
            ref={inputRef}
            placeholder="Ask about sections..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            sx={{ flex: 1 }}
            size="sm"
          />
          {isLoading ? (
            <Tooltip title={isStopping ? "Stopping..." : "Stop generating"}>
              <IconButton
                aria-label={isStopping ? "Stopping..." : "Stop generating"}
                onClick={cancelStream}
                disabled={isStopping}
                color="danger"
                variant="soft"
                size="sm"
              >
                <StopIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              onClick={handleSend}
              disabled={!inputValue.trim()}
              color="primary"
              variant="solid"
              size="sm"
            >
              <SendIcon />
            </IconButton>
          )}
        </Box>
        {stopError && <Alert color="danger">{stopError}</Alert>}
        <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.5 }}>
          Enter to send · Shift+Enter for new line
        </Typography>
      </Box>
    </Box>
  );
};

export default ChatPanel;
